/**
 * 媒体展示模式类型
 */
export type MediaMode = "youtube" | "bilibili" | "image";

/**
 * YouTube 连通性状态类型
 */
export type YouTubeStatus = "checking" | "available" | "unavailable";

/**
 * 从 header[data-feature-media] 上读取的 up-data
 */
interface FeatureMediaData {
  youtubeUrl: string;
  youtubePoster: string;
  bilibiliUrl: string;
  bilibiliPoster: string;
  imageUrl: string;
}

/**
 * FeatureMedia Unpoly 组件
 * 负责文章页头部媒体区域的展示逻辑：
 * - 采用 Click-to-Play 模式，初始显示封面图，点击后加载 iframe
 * - 通过探测 YouTube 连通性决定默认展示 YouTube 还是 Bilibili
 * - 支持多种媒体模式的手动切换
 */
export class FeatureMedia {
  /** 当前展示模式 */
  private mode: MediaMode | null = null;
  /** YouTube 连通性状态 */
  private youtubeStatus: YouTubeStatus = "checking";
  /** 是否已点击播放 */
  private playedYouTube = false;
  private playedBilibili = false;

  /** 缓存的 URL */
  private youtubeUrl: string;
  private bilibiliUrl: string;
  private imageUrl: string;

  /** DOM 引用 */
  private header: HTMLElement;
  /** 模式切换按钮 */
  private btnYoutube: HTMLElement | null;
  private btnBilibili: HTMLElement | null;
  private btnImage: HTMLElement | null;
  /** YouTube 连通性检测 spinner */
  private youtubeSpinner: HTMLElement | null;
  /** 媒体面板 */
  private panelYoutube: HTMLElement | null;
  private panelBilibili: HTMLElement | null;
  private panelImage: HTMLElement | null;
  /** 海报层 */
  private posterYoutube: HTMLElement | null;
  private posterBilibili: HTMLElement | null;

  constructor(header: HTMLElement, data: FeatureMediaData) {
    console.log("[FeatureMedia] 初始化组件", data);
    this.header = header;
    this.youtubeUrl = data.youtubeUrl || "";
    this.bilibiliUrl = data.bilibiliUrl || "";
    this.imageUrl = data.imageUrl || "";

    // 查询 DOM 元素
    this.btnYoutube = header.querySelector('[data-mode-btn="youtube"]');
    this.btnBilibili = header.querySelector('[data-mode-btn="bilibili"]');
    this.btnImage = header.querySelector('[data-mode-btn="image"]');
    this.youtubeSpinner = header.querySelector("[data-youtube-spinner]");

    this.panelYoutube = header.querySelector('[data-media-panel="youtube"]');
    this.panelBilibili = header.querySelector('[data-media-panel="bilibili"]');
    this.panelImage = header.querySelector('[data-media-panel="image"]');

    this.posterYoutube = header.querySelector('[data-poster="youtube"]');
    this.posterBilibili = header.querySelector('[data-poster="bilibili"]');

    this.init();
  }

  /**
   * 初始化：确定默认模式并触发 YouTube 连通性探测
   */
  private init() {
    // 默认展示逻辑：按 YouTube > Bilibili > Image 优先级排序
    if (this.youtubeUrl) {
      this.mode = "youtube";
    } else if (this.bilibiliUrl) {
      this.mode = "bilibili";
    } else if (this.imageUrl) {
      this.mode = "image";
    }

    console.log(`[FeatureMedia] 初始模式: ${this.mode}`);

    // 同步初始 UI 状态
    this.syncPanels();
    this.syncModeButtons();

    // 绑定模式切换按钮事件
    this.btnYoutube?.addEventListener("click", this.handleYoutubeClick);
    this.btnBilibili?.addEventListener("click", this.handleBilibiliClick);
    this.btnImage?.addEventListener("click", this.handleImageClick);

    // 绑定海报点击播放事件
    this.posterYoutube?.addEventListener(
      "click",
      this.handlePosterYoutubeClick,
    );
    this.posterBilibili?.addEventListener(
      "click",
      this.handlePosterBilibiliClick,
    );

    // YouTube 连通性探测
    if (this.youtubeUrl) {
      this.checkYouTubeConnectivity();
    } else {
      this.youtubeStatus = "unavailable";
      this.syncYoutubeStatusUI();
    }
  }

  // ── 事件处理器（箭头函数保持 this 绑定） ──

  private handleYoutubeClick = () => {
    console.log("[FeatureMedia] 点击切换 YouTube");
    if (this.youtubeStatus === "available") {
      this.showMode("youtube");
    }
  };

  private handleBilibiliClick = () => {
    console.log("[FeatureMedia] 点击切换 Bilibili");
    this.showMode("bilibili");
  };

  private handleImageClick = () => {
    console.log("[FeatureMedia] 点击切换图片");
    this.showMode("image");
  };

  private handlePosterYoutubeClick = () => {
    console.log("[FeatureMedia] 点击播放 YouTube");
    if (!this.playedYouTube) {
      this.playVideo("youtube");
    }
  };

  private handlePosterBilibiliClick = () => {
    console.log("[FeatureMedia] 点击播放 Bilibili");
    if (!this.playedBilibili) {
      this.playVideo("bilibili");
    }
  };

  // ── 核心逻辑 ──

  /**
   * 探测 YouTube 连通性
   * 使用 Image 对象探测 YouTube Favicon，超时 1.5s 则回退到备选模式
   */
  private async checkYouTubeConnectivity() {
    this.youtubeStatus = "checking";
    this.syncYoutubeStatusUI();

    const isAvailable = await new Promise<boolean>((resolve) => {
      const img = new Image();
      const timeout = setTimeout(() => {
        console.log("[FeatureMedia] ⏱ YouTube 连接超时，将回退到备选模式");
        img.onload = null;
        img.onerror = null;
        img.src = ""; // 停止加载
        resolve(false);
      }, 1500);

      img.onload = () => {
        clearTimeout(timeout);
        img.onload = null;
        img.onerror = null;
        console.log("[FeatureMedia] ✅ 已连接到 YouTube");
        resolve(true);
      };
      img.onerror = () => {
        clearTimeout(timeout);
        img.onload = null;
        img.onerror = null;
        console.log("[FeatureMedia] ❌ 无法连接到 YouTube");
        resolve(false);
      };
      // 利用 Image 对象请求 YouTube Favicon（带时间戳防止缓存）
      img.src = `https://www.youtube.com/favicon.ico?_=${Date.now()}`;
    }).catch((err) => {
      console.error("[FeatureMedia] 检测 YouTube 连通性失败:", err);
      return false;
    });

    this.youtubeStatus = isAvailable ? "available" : "unavailable";
    this.syncYoutubeStatusUI();

    // 如果探测失败且当前正是 youtube 模式，执行降级
    if (!isAvailable && this.mode === "youtube") {
      if (this.bilibiliUrl) {
        this.mode = "bilibili";
      } else if (this.imageUrl) {
        this.mode = "image";
      }
      this.syncPanels();
      this.syncModeButtons();
    }
  }

  /**
   * 切换当前的媒体模式
   */
  private showMode(mode: MediaMode) {
    const prevMode = this.mode;
    if (prevMode === mode) return;

    console.log(`[FeatureMedia] 切换模式: ${prevMode} -> ${mode}`);
    this.mode = mode;
    this.isLoadingVideo = false;

    // 如果从视频模式切走，重置播放状态以停止后台播放（销毁 iframe）
    if (prevMode === "youtube" && mode !== "youtube") {
      this.playedYouTube = false;
      this.resetVideoPanel("youtube");
    }
    if (prevMode === "bilibili" && mode !== "bilibili") {
      this.playedBilibili = false;
      this.resetVideoPanel("bilibili");
    }

    this.syncPanels();
    this.syncModeButtons();
  }

  /**
   * 触发视频播放（Click-to-Play）
   */
  private playVideo(mode: "youtube" | "bilibili") {
    console.log(`[FeatureMedia] 开始播放 ${mode}`);
    this.isLoadingVideo = true;

    if (mode === "youtube") {
      this.playedYouTube = true;
      this.injectIframe("youtube");
    }
    if (mode === "bilibili") {
      this.playedBilibili = true;
      this.injectIframe("bilibili");
    }
  }

  /**
   * 动态注入 iframe 到对应面板
   */
  private injectIframe(mode: "youtube" | "bilibili") {
    const panel = mode === "youtube" ? this.panelYoutube : this.panelBilibili;
    if (!panel) return;

    const iframeContainer = panel.querySelector(
      "[data-iframe-slot]",
    ) as HTMLElement | null;
    if (!iframeContainer) return;

    // 显示加载动画
    const poster =
      mode === "youtube" ? this.posterYoutube : this.posterBilibili;
    const loadingOverlay = poster?.querySelector(
      "[data-loading]",
    ) as HTMLElement | null;
    if (loadingOverlay) loadingOverlay.classList.remove("hidden");

    // 隐藏播放按钮
    const playBtn = poster?.querySelector(
      "[data-play-btn]",
    ) as HTMLElement | null;
    if (playBtn) playBtn.classList.add("hidden");

    // 移除海报上的 cursor-pointer
    if (poster) poster.classList.remove("cursor-pointer");

    // 构建 iframe
    const iframe = document.createElement("iframe");
    iframe.className = "absolute inset-0 w-full h-full rounded-lg";
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("allowfullscreen", "");

    if (mode === "youtube") {
      const title = this.header.dataset.videoTitle || "Video";
      iframe.title = title;
      iframe.src = `${this.youtubeUrl}?autoplay=1`;
      iframe.allow =
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
      iframe.referrerPolicy = "strict-origin-when-cross-origin";
    } else {
      const title = this.header.dataset.videoTitle || "Video";
      iframe.title = title;
      iframe.src = this.bilibiliUrl.replace("autoplay=0", "autoplay=1");
      iframe.allow =
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
    }

    iframe.addEventListener("load", () => this.handleVideoLoad(mode), {
      once: true,
    });

    iframeContainer.appendChild(iframe);
  }

  /**
   * 处理 iframe 加载完成
   */
  private handleVideoLoad(mode: "youtube" | "bilibili") {
    console.log(`[FeatureMedia] ${mode} iframe 加载完成`);
    this.isLoadingVideo = false;

    // 隐藏海报层，显示 iframe
    const poster =
      mode === "youtube" ? this.posterYoutube : this.posterBilibili;
    if (poster) {
      poster.classList.add("hidden");
    }
  }

  /**
   * 重置视频面板（切走时销毁 iframe）
   */
  private resetVideoPanel(mode: "youtube" | "bilibili") {
    const panel = mode === "youtube" ? this.panelYoutube : this.panelBilibili;
    if (!panel) return;

    // 移除 iframe
    const iframeContainer = panel.querySelector("[data-iframe-slot]");
    if (iframeContainer) iframeContainer.innerHTML = "";

    // 恢复海报层
    const poster =
      mode === "youtube" ? this.posterYoutube : this.posterBilibili;
    if (poster) {
      poster.classList.remove("hidden");
      poster.classList.add("cursor-pointer");

      // 恢复播放按钮
      const playBtn = poster.querySelector("[data-play-btn]");
      if (playBtn) playBtn.classList.remove("hidden");

      // 隐藏加载动画
      const loadingOverlay = poster.querySelector("[data-loading]");
      if (loadingOverlay) loadingOverlay.classList.add("hidden");
    }
  }

  // ── UI 同步方法 ──

  /**
   * 同步媒体面板的显示/隐藏
   */
  private syncPanels() {
    const panels: [HTMLElement | null, MediaMode][] = [
      [this.panelYoutube, "youtube"],
      [this.panelBilibili, "bilibili"],
      [this.panelImage, "image"],
    ];

    for (const [panel, mode] of panels) {
      if (!panel) continue;
      if (this.mode === mode) {
        panel.classList.remove("hidden");
        // 进入动画
        requestAnimationFrame(() => {
          panel.classList.remove("opacity-0", "scale-95");
          panel.classList.add("opacity-100", "scale-100");
        });
      } else {
        panel.classList.add("opacity-0", "scale-95");
        panel.classList.remove("opacity-100", "scale-100");
        // 等过渡结束后隐藏
        setTimeout(() => {
          if (this.mode !== mode) {
            panel.classList.add("hidden");
          }
        }, 300);
      }
    }
  }

  /**
   * 同步模式切换按钮的激活状态
   */
  private syncModeButtons() {
    const buttons: [HTMLElement | null, MediaMode][] = [
      [this.btnYoutube, "youtube"],
      [this.btnBilibili, "bilibili"],
      [this.btnImage, "image"],
    ];

    for (const [btn, mode] of buttons) {
      if (!btn) continue;
      if (this.mode === mode) {
        btn.classList.add("opacity-100", "text-primary");
        btn.classList.remove("opacity-40");
      } else {
        btn.classList.remove("opacity-100", "text-primary");
        btn.classList.add("opacity-40");
      }
    }
  }

  /**
   * 同步 YouTube 连通性状态的 UI
   */
  private syncYoutubeStatusUI() {
    // spinner 显示/隐藏
    if (this.youtubeSpinner) {
      if (this.youtubeStatus === "checking") {
        this.youtubeSpinner.classList.remove("hidden");
      } else {
        this.youtubeSpinner.classList.add("hidden");
      }
    }

    // 按钮状态
    if (this.btnYoutube) {
      if (this.youtubeStatus === "checking") {
        this.btnYoutube.classList.add("hidden");
      } else {
        this.btnYoutube.classList.remove("hidden");
      }

      if (this.youtubeStatus === "unavailable") {
        this.btnYoutube.classList.add(
          "cursor-not-allowed",
          "opacity-10",
          "grayscale",
          "scale-95",
        );
        this.btnYoutube.classList.remove("cursor-pointer");
        this.btnYoutube.title = "YouTube (无法连接)";
      } else if (this.youtubeStatus === "available") {
        this.btnYoutube.classList.remove(
          "cursor-not-allowed",
          "opacity-10",
          "grayscale",
          "scale-95",
        );
        this.btnYoutube.classList.add("cursor-pointer");
        this.btnYoutube.title = "YouTube";
      }
    }
  }

  /**
   * 清理事件监听器
   */
  destroy() {
    console.log("[FeatureMedia] 销毁组件");
    this.btnYoutube?.removeEventListener("click", this.handleYoutubeClick);
    this.btnBilibili?.removeEventListener("click", this.handleBilibiliClick);
    this.btnImage?.removeEventListener("click", this.handleImageClick);
    this.posterYoutube?.removeEventListener(
      "click",
      this.handlePosterYoutubeClick,
    );
    this.posterBilibili?.removeEventListener(
      "click",
      this.handlePosterBilibiliClick,
    );
  }

  /**
   * 注册 Unpoly 编译器
   */
  static compiler(selector: string) {
    up.compiler(selector, (element: Element, data: FeatureMediaData) => {
      const instance = new FeatureMedia(element as HTMLElement, data);
      return () => instance.destroy();
    });
  }
}
